import { useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { Tooltip } from '../../../../components/Tooltip';
import { DataTable, type DataColumn } from '../../../../components/data-table/DataTable';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { StackedCell } from '../../../../components/table/StackedCell';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { toDateTimeLocal } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceItemsApi, evidenceStorageLocationsApi } from '../evidenceApi';
import {
  CUSTODY,
  CUSTODY_FACET,
  CUSTODY_LABEL,
  EVALUATION,
  type EvidenceEvaluationDecision,
  type EvidenceContainerGroup,
  type EvidenceItemListItem,
  type EvidenceStorageLocationOption,
} from '../evidenceTypes';
import { useStaffOptions } from '../../../human-resources/personnel/useStaff';
import { EvidencePlaceField } from './EvidencePlaceField';
import { sampleEvaluation } from '../utils/sampleEvidence';

const ID = 'evidence-evaluate';

const DEFER_REASONS = [
  'Too large for the evidence room',
  'Hazardous — kept at a secure facility',
  'Perishable — kept in cold storage',
  'Vehicle or vessel — kept at the impound yard',
];

/*
  A storage location typed into the picker is STAGED, not written — the `<InventoryItemModal>`
  rule. Created under its own key immediately before the evaluation, so abandoning the form leaves
  no location behind.
*/
const PENDING_LOCATION = 'pending:location:';

type Step = 'decision' | 'details';
const STEPS: Step[] = ['decision', 'details'];
const STEP_LABEL: Record<Step, string> = { decision: 'Accept or defer', details: 'Items and details' };

/** Which step owns each field the server can name — a refusal goes to its step. */
const FIELD_STEP: Record<string, Step> = { decision: 'decision' };

/**
 * Step 2 of the cycle — EVALUATION (product call, 08 Oct 2026). Every listed item gets ONE
 * decision, all or nothing — in TWO STEPS (product call, 08 Oct 2026):
 *
 * 1. **Accept or defer.**
 * 2. **Items and details** — the items being evaluated in a TABLE, with a search ABOVE it to add
 *    another of the case's waiting items (the Share dialog's shape), then the decision's fields
 *    (accept: container/box no. and storage location; defer: reason and transfer-to place) and the
 *    entered audit (evaluated by/at, stored by/at, defaulting to the signed-in employee and now).
 *
 * ONLY items FOR EVALUATION are offered (10 Oct 2026) — a deferred item comes back by Store-in, not here;
 * the server refuses any other. The anyDeferred guard below is now inert and kept as a backstop for a
 * stale preset. No approval and no password: evaluating is part
 * of receiving, and every decision is a line on the item's chain of custody.
 */
export function EvidenceEvaluateModal({
  items: initialItems,
  onClose,
  onSaved,
}: {
  items: EvidenceItemListItem[];
  onClose: () => void;
  /** The items actually evaluated — the reader may have added to the ones the form opened with. */
  onSaved: (evaluated: EvidenceItemListItem[]) => void;
}) {
  const canCreateLocation = usePermission('evidence-storage-locations.create');
  const canReadPersonnel = usePermission('personnel.read');
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);
  const caseId = initialItems[0]?.evidenceCaseId;

  const [stepIndex, setStepIndex] = useState(0);
  const [items, setItems] = useState<EvidenceItemListItem[]>(initialItems);
  const [decision, setDecision] = useState<string>(String(EVALUATION.accept));
  const [containerNumber, setContainerNumber] = useState('');
  const [locationId, setLocationId] = useState('');
  const [reason, setReason] = useState('');
  const [place, setPlace] = useState('');
  const [evaluatedById, setEvaluatedById] = useState('');
  const [evaluatedAt, setEvaluatedAt] = useState(() => toDateTimeLocal(new Date()));
  const [storedById, setStoredById] = useState('');
  const [storedAt, setStoredAt] = useState(() => toDateTimeLocal(new Date()));

  const [candidates, setCandidates] = useState<EvidenceItemListItem[]>([]);
  const [locations, setLocations] = useState<EvidenceStorageLocationOption[]>([]);
  const [boxes, setBoxes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    // Failed loads name no field, so they never reach the save path's error state.
    void Promise.all([
      evidenceStorageLocationsApi.options(controller.signal).catch(() => [] as EvidenceStorageLocationOption[]),
      caseId
        ? evidenceItemsApi.containers(caseId, undefined, controller.signal).catch(() => [] as EvidenceContainerGroup[])
        : Promise.resolve([] as EvidenceContainerGroup[]),
      caseId
        ? evidenceItemsApi
            .list(
              {
                caseId,
                // ONLY what WAITS — Evaluate is for items for evaluation (09 Oct 2026).
                custody: CUSTODY_FACET[CUSTODY.forEvaluation],
                pageSize: 100,
                page: 1,
              },
              controller.signal,
            )
            .then((page) => page.items)
            .catch(() => [] as EvidenceItemListItem[])
        : Promise.resolve([] as EvidenceItemListItem[]),
    ]).then(([nextLocations, nextBoxes, nextItems]) => {
      if (controller.signal.aborted) return;
      setLocations(nextLocations);
      setBoxes(nextBoxes.map((b) => b.containerNumber).filter((n): n is string => n !== null));
      setCandidates(nextItems.filter((i) => i.isActive));
      setLoading(false);
    });

    // THE SIGNED-IN EMPLOYEE is the default evaluator and storer — only while nobody was chosen.
    evidenceItemsApi
      .eligibility(controller.signal)
      .then((eligibility) => {
        const mine = eligibility.personnelId;
        if (controller.signal.aborted || !mine) return;
        setEvaluatedById((current) => current || mine);
        setStoredById((current) => current || mine);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [caseId]);

  const accepting = Number(decision) === EVALUATION.accept;
  const anyDeferred = items.some((i) => i.status === CUSTODY.deferred);

  // Another item may be added — a waiting one always; a deferred one only when ACCEPTING.
  const listed = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  const itemOptions = candidates
    .filter((c) => !listed.has(c.id) && (accepting || c.status === CUSTODY.forEvaluation))
    .map((c) => ({
      value: c.id,
      label: `${c.typeName ?? 'Item'} — ${c.description}`,
      sublabel: `${c.inventoryNumber} · ${CUSTODY_LABEL[c.status]}`,
      keywords: c.inventoryNumber,
    }));

  const decisionMissing = !accepting && anyDeferred ? 'A deferred item is listed — it can only be accepted.' : null;

  const detailsMissing =
    items.length === 0
      ? 'Add at least one item.'
      : accepting
        ? containerNumber.trim() === ''
          ? 'Enter the container/box no.'
          : locationId === ''
            ? 'Choose the storage location.'
            : null
        : reason.trim() === ''
          ? 'Give the reason it is deferred.'
          : place.trim() === ''
            ? 'Enter the transfer-to place.'
            : null;

  const auditMissing =
    evaluatedById === ''
      ? 'Choose who evaluated it.'
      : evaluatedAt === ''
        ? 'Enter when it was evaluated.'
        : storedById === ''
          ? 'Choose who stored it.'
          : storedAt === ''
            ? 'Enter when it was stored.'
            : null;

  const missingOn: Record<Step, string | null> = { decision: decisionMissing, details: detailsMissing ?? auditMissing };
  const currentStep = STEPS[Math.min(stepIndex, STEPS.length - 1)];
  const isLastStep = currentStep === 'details';
  const firstGap = STEPS.map((s) => missingOn[s]).find((m) => m !== null) ?? null;
  const canSave = firstGap === null && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      let resolvedLocation = locationId;
      const stagedName = locationId.startsWith(PENDING_LOCATION) ? locationId.slice(PENDING_LOCATION.length) : null;

      // The staged location first, under its OWN key — never folded into the evaluation.
      if (accepting && stagedName !== null) {
        const created = await evidenceStorageLocationsApi.create({ name: stagedName, description: null });
        resolvedLocation = created.id;
        setLocationId(created.id);
        setLocations((current) => [...current, { id: created.id, code: created.code, name: created.name }]);
      }

      await evidenceItemsApi.evaluate({
        itemIds: items.map((i) => i.id),
        decision: Number(decision) as EvidenceEvaluationDecision,
        containerNumber: accepting ? containerNumber.trim() : null,
        storageLocationId: accepting ? resolvedLocation : null,
        reason: accepting ? null : reason.trim(),
        place: accepting ? null : place.trim(),
        evaluatedByPersonnelId: evaluatedById,
        evaluatedAt: new Date(evaluatedAt).toISOString(),
        storedByPersonnelId: storedById,
        storedAt: new Date(storedAt).toISOString(),
      });

      onSaved(items);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the evaluation.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: ID,
        // A RadioGroup puts its id on each OPTION, so the field maps to the chosen one.
        fieldIds: { decision: `${ID}-decision-${decision}` },
        stepOf: (field) => STEPS.indexOf(FIELD_STEP[field] ?? 'details'),
        onStep: setStepIndex,
      });
    } finally {
      setSaving(false);
    }
  }

  const stagedLocation = locationId.startsWith(PENDING_LOCATION) ? locationId.slice(PENDING_LOCATION.length) : null;
  const locationOptions = [
    // The staged name, so the field states what the reader typed while the row does not exist yet.
    ...(stagedLocation !== null ? [{ value: locationId, label: stagedLocation }] : []),
    ...locations.map((l) => ({ value: l.id, label: l.name, sublabel: l.code, keywords: l.code })),
  ];

  const subject = items.length === 1 ? items[0].inventoryNumber : `${items.length} items`;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Evaluate ${subject}`}
      size="3xl"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleEvaluation({
            locationIds: locations.map((l) => l.id),
            staffIds: staff.map((s) => s.value),
          });
          setContainerNumber(sample.containerNumber);
          setLocationId(sample.storageLocationId || (canCreateLocation ? `${PENDING_LOCATION}Sample shelf` : ''));
          setReason(sample.reason);
          setPlace(sample.place);
          setEvaluatedById(sample.evaluatedByPersonnelId);
          setEvaluatedAt(sample.evaluatedAt);
          setStoredById(sample.storedByPersonnelId);
          setStoredAt(sample.storedAt);
        },
        disabled: saving || loading || loadingStaff,
      }}
      confirm={isLastStep ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {stepIndex > 0 ? (
            <Button id={`${ID}-back`} variant="secondary" onClick={() => setStepIndex((s) => Math.max(0, s - 1))} disabled={saving}>
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Tooltip disabledReason={missingOn[currentStep]}>
              <Button
                id={`${ID}-next`}
                variant="secondary"
                onClick={() => setStepIndex((s) => Math.min(STEPS.length - 1, s + 1))}
                disabled={missingOn[currentStep] !== null}
              >
                Next
              </Button>
            </Tooltip>
          ) : null}

          <Tooltip disabledReason={firstGap}>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Stepper
          id={ID}
          steps={STEPS.map((key) => ({ key, label: STEP_LABEL[key] }))}
          activeIndex={stepIndex}
          onJump={(index) => {
            if (index <= stepIndex || STEPS.slice(0, index).every((s) => missingOn[s] === null)) setStepIndex(index);
          }}
        />

        <div id={`${ID}-panel-${currentStep}`} role="tabpanel" aria-labelledby={`${ID}-step-${currentStep}`} className="flex flex-col gap-4">
          {currentStep === 'decision' ? (
            <RadioGroup
              id={`${ID}-decision`}
              label="Decision"
              required
              // One option per row — product call, 08 Oct 2026.
              columns={1}
              value={decision}
              onChange={setDecision}
              disabled={saving}
              options={[
                {
                  value: String(EVALUATION.accept),
                  label: 'Accept',
                  description: 'Stored in the evidence room, in a container, at a storage location.',
                },
                {
                  value: String(EVALUATION.defer),
                  label: 'Defer',
                  description: anyDeferred
                    ? 'Not offered — at least one listed item is already deferred. A deferred item can only be accepted.'
                    : 'Kept outside the facility, for a reason, at a named place. It comes back by a store-in.',
                  disabled: anyDeferred,
                },
              ]}
              error={error?.fieldError('decision')}
            />
          ) : (
            <>
              {/*
                THE ITEMS BEING EVALUATED, in a table — product call, 08 Oct 2026: *display the evaluated
                item in a table, i must be able to add another item*. The search above it ADDS a row.
              */}
              <div className="flex flex-col gap-2">
                <Select
                  id={`${ID}-item-ids`}
                  label="Items"
                  required
                  description={
                    accepting
                      ? "Add another of this case's items waiting for evaluation to accept it with these."
                      : "Add another of this case's items waiting for evaluation to defer it with these."
                  }
                  placeholder={
                    !loading && itemOptions.length === 0
                      ? 'No other item of this case can be added'
                      : 'Search items by type, description or inventory no.…'
                  }
                  value=""
                  onChange={(next) => {
                    const picked = candidates.find((c) => c.id === next);
                    if (picked && !listed.has(picked.id)) setItems((current) => [...current, picked]);
                  }}
                  options={itemOptions}
                  remote
                  loading={loading}
                  disabled={saving || (!loading && itemOptions.length === 0)}
                  error={error?.fieldError('itemIds')}
                />

                <DataTable<EvidenceItemListItem>
                  id={`${ID}-items`}
                  columns={ITEM_COLUMNS}
                  rows={items}
                  rowKey={(row) => row.id}
                  sort={null}
                  onToggleSort={() => undefined}
                  onDelete={saving ? undefined : (row) => setItems((current) => current.filter((i) => i.id !== row.id))}
                  deleteLabel={() => 'Remove'}
                  emptyMessage="No item listed. Search above and pick the items to evaluate."
                />
              </div>

              {accepting ? (
                /*
                  CONTAINER/BOX NO AND STORAGE LOCATION SHARE A ROW — product call, 09 Oct 2026, replacing
                  "one field per row" for these two. The box is a DROPDOWN of the case's existing boxes that
                  takes a new one typed in — UPPERCASE, as the server stores it (one box, one spelling).
                */
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    id={`${ID}-container-number`}
                    label="Container/Box No"
                    required
                    description="The container or box the items are kept in. Pick one of this case's boxes, or type a new one. The case's Stored in folder groups by it."
                    placeholder="Choose or type a box"
                    value={containerNumber}
                    onChange={(next) => setContainerNumber(next.toUpperCase())}
                    options={[
                      ...(containerNumber && !boxes.includes(containerNumber) ? [{ value: containerNumber, label: containerNumber }] : []),
                      ...boxes.map((b) => ({ value: b, label: b })),
                    ]}
                    remote
                    loading={loading}
                    allowCustom
                    onCreateOption={(raw) => setContainerNumber(raw.trim().toUpperCase().slice(0, 50))}
                    disabled={saving}
                    error={error?.fieldError('containerNumber')}
                  />

                  <Select
                    id={`${ID}-storage-location-id`}
                    label="Storage location"
                    required
                    description={
                      canCreateLocation
                        ? 'The exact spot in the evidence room where they are kept. Pick one, or type a new one.'
                        : 'The exact spot in the evidence room where they are kept.'
                    }
                    placeholder="Choose a location"
                    value={locationId}
                    onChange={setLocationId}
                    options={locationOptions}
                    remote
                    loading={loading}
                    allowCustom={canCreateLocation}
                    onCreateOption={canCreateLocation ? (raw) => setLocationId(`${PENDING_LOCATION}${raw.trim()}`) : undefined}
                    disabled={saving}
                    error={error?.fieldError('storageLocationId')}
                  />
                </div>
              ) : (
                <>
                  <Select
                    id={`${ID}-reason`}
                    label="Reason"
                    required
                    description="Why it is kept outside the facility. Pick one, or type your own."
                    placeholder="Choose or type a reason"
                    value={reason}
                    onChange={setReason}
                    options={DEFER_REASONS.map((r) => ({ value: r, label: r }))}
                    sort="none"
                    allowCustom
                    disabled={saving}
                    error={error?.fieldError('reason')}
                  />

                  <EvidencePlaceField
                    id={`${ID}-place`}
                    caseId={caseId}
                    description="Where it is kept outside the facility. Pick a place this case has used, or type a new one."
                    value={place}
                    onChange={setPlace}
                    disabled={saving}
                    error={error?.fieldError('place')}
                  />
                </>
              )}

              {/* THE AUDIT, entered — TWO COLUMNS (product call, 09 Oct 2026): who beside when, evaluated then stored. */}
              <div className="grid gap-4 sm:grid-cols-2">
                {canReadPersonnel ? (
                  <Select
                    id={`${ID}-evaluated-by-personnel-id`}
                    label="Evaluated by"
                    required
                    description="The employee who evaluated the items. Defaults to you."
                    placeholder="Choose an employee"
                    value={evaluatedById}
                    onChange={setEvaluatedById}
                    options={staff}
                    remote
                    loading={loadingStaff}
                    disabled={saving}
                    error={error?.fieldError('evaluatedByPersonnelId')}
                  />
                ) : null}

                <TextField
                  id={`${ID}-evaluated-at`}
                  label="Evaluated at"
                  required
                  type="datetime-local"
                  description="When they were evaluated. It cannot be in the future."
                  value={evaluatedAt}
                  onChange={(e) => setEvaluatedAt(e.target.value)}
                  disabled={saving}
                  className="font-mono tabular-nums"
                  error={error?.fieldError('evaluatedAt')}
                />

                {canReadPersonnel ? (
                  <Select
                    id={`${ID}-stored-by-personnel-id`}
                    label="Stored by"
                    required
                    description={
                      accepting
                        ? 'The employee who put the items in the evidence room. Defaults to you.'
                        : 'The employee who took the items to the place outside the facility. Defaults to you.'
                    }
                    placeholder="Choose an employee"
                    value={storedById}
                    onChange={setStoredById}
                    options={staff}
                    remote
                    loading={loadingStaff}
                    disabled={saving}
                    error={error?.fieldError('storedByPersonnelId')}
                  />
                ) : null}

                <TextField
                  id={`${ID}-stored-at`}
                  label="Stored at"
                  required
                  type="datetime-local"
                  description="When they were stored. It cannot be in the future."
                  value={storedAt}
                  onChange={(e) => setStoredAt(e.target.value)}
                  disabled={saving}
                  className="font-mono tabular-nums"
                  error={error?.fieldError('storedAt')}
                />
              </div>

              <p className="text-xs text-slate-500">
                Applies to {items.length === 1 ? 'this item' : `all ${items.length} items`}, together — if one
                cannot be evaluated, none is. Each decision is a line on the item's chain of custody.
              </p>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

// What it is and where it stands now — so the reader can check the list before deciding for all of it.
const ITEM_COLUMNS: DataColumn<EvidenceItemListItem>[] = [
  { key: 'type', header: 'Type', width: 140, render: (row) => <TextCell value={row.typeName} /> },
  // The description with the INVENTORY NO. under it — product call, 09 Oct 2026; the case tables' evidence column.
  {
    key: 'description',
    header: 'Description',
    width: 420,
    render: (row) => (
      <StackedCell
        primary={row.description}
        secondary={row.storageLocationName ? `${row.inventoryNumber} · ${row.storageLocationName}` : row.inventoryNumber}
        mono
      />
    ),
  },
  { key: 'custody', header: 'Custody', width: 140, render: (row) => <TextCell value={CUSTODY_LABEL[row.status]} /> },
];
